import type { AudioRecordingRepository } from "@/domains/dexie-db/repositories";
import type {
  AudioRecordingChannelData,
  AudioRecordingState,
} from "@/domains/dexie-db/models/audio-recording";
import {
  interleaveTranscriptInfo,
  transcriptArray,
  transcriptString,
  type TranscriptChannelParts,
} from "@/store/audio-capture/transcription/utils/transcript-interleaver";
import { isFinite, toNumber } from "lodash-es";
import type { TranscriptArrayItem } from "@/store/audio-capture/transcription/utils/types";
import { assembleAudioBlobFromChunks } from "@/store/audio-capture/processing/utils/assemble-audio-blob";

export interface DexieRecordingPayload {
  noteId: string;
  recordingId: string;
  capturedAudioId: string;
  audioBlob?: Blob;
  transcriptText: string;
  transcriptArray: TranscriptArrayItem[];
  preferredMimeType?: string;
}

interface LoadDexieRecordingArgs {
  repository: AudioRecordingRepository;
  recordingId?: string | null;
  noteId?: string | null;
  requireAudio?: boolean;
}

export async function loadDexieRecordingPayload({
  repository,
  recordingId,
  noteId,
  requireAudio = true,
}: LoadDexieRecordingArgs): Promise<DexieRecordingPayload | null> {
  const state = await resolveRecordingState({ repository, recordingId, noteId });
  if (!state) return null;

  const resolvedRecordingId = state.recordingId ?? recordingId;
  const resolvedNoteId = state.noteId ?? noteId ?? undefined;
  const capturedAudioId = state.capturedAudioId;

  if (!resolvedRecordingId || !resolvedNoteId || !capturedAudioId) {
    return null;
  }

  const chunks = state.audioBlobs.map(entry => entry.blobData).filter(Boolean) as Blob[];
  const transcriptItems = buildTranscriptItems(state.channels ?? []);

  const payloadBase: Omit<DexieRecordingPayload, "audioBlob" | "preferredMimeType"> = {
    noteId: resolvedNoteId,
    recordingId: resolvedRecordingId,
    capturedAudioId,
    transcriptText: transcriptString(transcriptItems),
    transcriptArray: transcriptArray(transcriptItems),
  };

  if (!requireAudio) {
    return payloadBase;
  }

  const audioBlob = await assembleAudioBlobFromChunks(chunks);
  if (!audioBlob) {
    return null;
  }

  return {
    ...payloadBase,
    audioBlob,
    preferredMimeType: audioBlob.type || undefined,
  };
}

export async function resolveRecordingState({
  repository,
  recordingId,
  noteId,
}: LoadDexieRecordingArgs): Promise<AudioRecordingState | null | undefined> {
  if (recordingId) {
    const state = await repository.getRunRecordingState({ recordingId });
    if (state) return state;
  }

  if (noteId) {
    return repository.getRecordingState({ noteId });
  }

  return null;
}

function buildTranscriptItems(
  channels: AudioRecordingChannelData[]
): ReturnType<typeof interleaveTranscriptInfo> {
  const channelParts: TranscriptChannelParts[] = channels
    .map(channel => {
      const parts = createPartsForChannel(channel);
      if (parts.length === 0) return null;
      return { channel: channel.channel ?? 0, parts } satisfies TranscriptChannelParts;
    })
    .filter((entry): entry is TranscriptChannelParts => entry !== null);

  if (channelParts.length === 0) {
    return [];
  }

  return interleaveTranscriptInfo(channelParts);
}

function createPartsForChannel(
  channel: AudioRecordingChannelData
): TranscriptChannelParts["parts"] {
  const parts: TranscriptChannelParts["parts"] = [];

  if (channel.transcriptChunks.length > 0) {
    parts.push(dedupeTranscriptChunks(channel.transcriptChunks));
  }

  if (parts.length === 0 && channel.snapshots.length > 0) {
    const latestSnapshot = channel.snapshots[channel.snapshots.length - 1];
    if (latestSnapshot?.transcripts?.length) {
      parts.push(latestSnapshot.transcripts);
    }
  }

  return parts;
}

function dedupeTranscriptChunks(
  chunks: AudioRecordingChannelData["transcriptChunks"]
): AudioRecordingChannelData["transcriptChunks"] {
  if (chunks.length <= 1) return chunks;

  const chunksByTurn = new Map<number, AudioRecordingChannelData["transcriptChunks"][number]>();
  const chunksWithoutTurnOrder: AudioRecordingChannelData["transcriptChunks"] = [];

  for (const chunk of chunks) {
    const turnOrder = toFiniteNumber(chunk.turn_order);
    if (turnOrder === null) {
      chunksWithoutTurnOrder.push(chunk);
      continue;
    }

    const existingChunk = chunksByTurn.get(turnOrder);
    if (existingChunk == null) {
      chunksByTurn.set(turnOrder, chunk);
      continue;
    }

    const preferredChunk = pickPreferredChunk(existingChunk, chunk);
    chunksByTurn.set(turnOrder, preferredChunk);
  }

  const dedupedChunks = Array.from(chunksByTurn.entries())
    .sort(([leftTurnOrder], [rightTurnOrder]) => leftTurnOrder - rightTurnOrder)
    .map(([, chunk]) => chunk);

  return [...dedupedChunks, ...chunksWithoutTurnOrder];
}

function toFiniteNumber(value: unknown): number | null {
  const numericValue = toNumber(value);
  return isFinite(numericValue) ? numericValue : null;
}

function pickPreferredChunk(
  existingChunk: AudioRecordingChannelData["transcriptChunks"][number],
  incomingChunk: AudioRecordingChannelData["transcriptChunks"][number]
): AudioRecordingChannelData["transcriptChunks"][number] {
  const existingIsFinal = existingChunk.end_of_turn === true;
  const incomingIsFinal = incomingChunk.end_of_turn === true;

  if (incomingIsFinal && !existingIsFinal) {
    return incomingChunk;
  }

  if (existingIsFinal && !incomingIsFinal) {
    return existingChunk;
  }

  const existingTimestamp = existingChunk.timestamp ?? 0;
  const incomingTimestamp = incomingChunk.timestamp ?? 0;

  if (incomingTimestamp > existingTimestamp) {
    return incomingChunk;
  }

  if (incomingTimestamp < existingTimestamp) {
    return existingChunk;
  }

  const existingLength = existingChunk.transcript?.length ?? 0;
  const incomingLength = incomingChunk.transcript?.length ?? 0;

  if (incomingLength >= existingLength) {
    return incomingChunk;
  }

  return existingChunk;
}
